import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
} from "@ionic/react";

// Question Type Definitions
interface BaseQuestion {
    type: string;
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    correct: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[]; // Fill-in options for user to choose
    correct: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    correct: boolean;
}

interface MatchThePairQuestion extends BaseQuestion {
    type: "match";
    options: string[];
    labeled: { "A": string[], "B": string[] };
    correct: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion
    | MatchThePairQuestion;

// Define types
type SyllabusType =
    | "Numerals"
    | "NumberNames"
    | "NumberSense"
    | "ComputationOperations"
    | "Length"
    | "Weight"
   // | "Capacity"
    | "Time"
   // | "Temperature"
    | "Money"
    | "Lines"
    | "ShapesAndSolids"
    | "Patterns";


interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
    syllabus?: SyllabusType;
}



const syllabusFiles: Record<SyllabusType, string> = {
    "Numerals": "Numerals.json",
    "NumberNames": "NumberNames.json",
    "NumberSense": "NumberSense.json",
    "ComputationOperations": "ComputationOperations.json",
    "Length": "Length.json",
    "Weight": "Weight.json",
   // "Capacity": "Capacity.json",
    "Time": "Time.json",
   // "Temperature": "Temperature.json",
    "Money": "Money.json",
    "Lines": "Lines.json",
    "ShapesAndSolids": "ShapesAndSolids.json",
    "Patterns": "Patterns.json",
}

const IMO: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);

    const loadRandomSyllabusQuestions = async () => {
        const topics = Object.keys(syllabusFiles) as SyllabusType[];
        const randomTopic = topics[Math.floor(Math.random() * topics.length)];
        const filename = syllabusFiles[randomTopic];

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "IMOClassOneSyllabus/" + filename
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/IMOClassOneSyllabus/${filename}`
                );
                data = module.default;
            }
            setQuestions(data);
            const choosedOne = data[Math.floor(Math.random() * data.length)];
            setCurrentQuestion(choosedOne);
            //console.log(currentQuestion)
            if (choosedOne?.type === 'match') {
                setMatchOption(shuffleArray(choosedOne?.labeled.B))
            }
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.correct;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false" || currentQuestion.type === "match") {
            correctAnswer = currentQuestion.correct;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                loadRandomSyllabusQuestions();
                setSelectedOption(null);
            }, 4000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Mamas Guidance - Olympiad - Class Two - International General Knowledge Olympiad (IGKO)";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 1 IGKO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.correct).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                            {currentQuestion.type === "match" && (
                                <>
                                    <table className="table-bordered table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Column A</th>
                                                <th scope="col">Column B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentQuestion.labeled.A.map((leftItem, index) => (
                                                <tr key={index}>
                                                    <td>{leftItem}</td>
                                                    <td>{currentQuestion.labeled.B[index]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default IMO;
